import React, { useState, useEffect, useCallback } from "react";
import { speakText } from "../utils/speech";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import HearingIcon from "@mui/icons-material/Hearing";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import "../styles/MeaningMatch.css";

function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

const QUIZ_LENGTH = 10;

export default function MeaningMatchGame({ words, practiceType, forcedMode }) {
    const [questions, setQuestions] = useState([]);
    const [currentQ, setCurrentQ] = useState(0);
    const [selected, setSelected] = useState(null);
    const [results, setResults] = useState([]);
    const [finished, setFinished] = useState(false);
    const [selectedMode, setSelectedMode] = useState(forcedMode || "recognition");

    const isChinese = practiceType === "chinese";

    const hasChineseMeanings = isChinese && words.length > 0 &&
        !!(words[0]?.english?.trim() || words[0]?.pinyin?.trim());
    const hasEnglishMeanings = !isChinese && words.length > 0 && !!words[0]?.meaningEn;
    const hasMeaningMode = hasChineseMeanings || hasEnglishMeanings;

    const speak = useCallback((text) => {
        speakText(text, isChinese ? 'zh' : 'en');
    }, [isChinese]);

    const buildQuestions = useCallback(() => {
        if (words.length < 2) return;

        const pool = shuffle(words).slice(0, Math.min(QUIZ_LENGTH, words.length));

        const qs = pool.map((word) => {
            let questionText, correct, getOption, questionPinyin = "", chineseSubMode = "";
            let effectiveQuizMode;

            if (isChinese) {
                if (selectedMode === "meaning" && hasChineseMeanings) {
                    questionText = word.chinese;
                    correct = word.english || word.pinyin;
                    getOption = (w) => w.english || w.pinyin;
                    questionPinyin = word.pinyin || "";
                    chineseSubMode = "meaning";
                    effectiveQuizMode = "chinese";
                } else {
                    questionText = word.chinese;
                    correct = word.chinese;
                    getOption = (w) => w.chinese;
                    chineseSubMode = "recognition";
                    effectiveQuizMode = "chinese";
                }
            } else if (selectedMode === "meaning" && hasEnglishMeanings) {
                questionText = word.word;
                correct = word.meaningEn;
                getOption = (w) => w.meaningEn;
                effectiveQuizMode = "meaning";
            } else {
                questionText = word.word;
                correct = word.word;
                getOption = (w) => w.word;
                effectiveQuizMode = "recognition";
            }

            const others = words.filter((w) =>
                isChinese ? w.chinese !== word.chinese : w.word !== word.word
            );

            const wrongs = shuffle(others)
                .filter((w) => getOption(w) && getOption(w).trim() && getOption(w) !== correct)
                .slice(0, 3)
                .map(getOption);

            const choices = shuffle([correct, ...wrongs]);
            return { questionText, questionPinyin, chineseSubMode, correct, choices, quizMode: effectiveQuizMode };
        });

        setQuestions(qs);
        setCurrentQ(0);
        setSelected(null);
        setResults([]);
        setFinished(false);
    }, [words, isChinese, selectedMode, hasChineseMeanings, hasEnglishMeanings]);

    useEffect(() => {
        buildQuestions();
    }, [buildQuestions]);

    useEffect(() => {
        if (!questions.length || finished) return;
        const q = questions[currentQ];
        if (!q) return;
        if (q.quizMode === "recognition") {
            speak(q.questionText, "en-SG");
        } else if (q.quizMode === "chinese" && q.chineseSubMode === "recognition") {
            speak(q.questionText, "zh-CN");
        }
    }, [currentQ, questions, finished, speak]);

    const handleSelect = (choice) => {
        if (selected !== null) return;
        const correct = choice === questions[currentQ].correct;
        setSelected(choice);
        setResults((prev) => [...prev, correct]);

        setTimeout(() => {
            if (currentQ + 1 >= questions.length) {
                setFinished(true);
            } else {
                setCurrentQ((q) => q + 1);
                setSelected(null);
            }
        }, 1100);
    };

    const handleModeChange = (mode) => {
        if (mode === selectedMode) return;
        setSelectedMode(mode);
    };

    if (words.length < 2) {
        return (
            <div className="quiz-empty">
                <div className="quiz-empty-icon">📚</div>
                <p>Need at least 2 words to play!</p>
            </div>
        );
    }

    if (finished) {
        const score = results.filter(Boolean).length;
        const total = results.length;
        const pct = Math.round((score / total) * 100);
        const emoji = pct === 100 ? "🌟" : pct >= 80 ? "⭐" : pct >= 60 ? "😊" : "📚";
        const message =
            pct === 100 ? "Perfect! Amazing!" :
            pct >= 80 ? "Excellent!" :
            pct >= 60 ? "Good job!" : "Keep practicing!";

        return (
            <div className="quiz-finished">
                <div className="quiz-finished-emoji">{emoji}</div>
                <h2 className="quiz-finished-title">{message}</h2>
                <div className="quiz-score-circle">
                    <span className="quiz-score-num">{score}</span>
                    <span className="quiz-score-sep">/</span>
                    <span className="quiz-score-tot">{total}</span>
                </div>
                <div className="quiz-star-row">
                    {results.map((r, i) => (
                        <StarIcon key={i} className={r ? "quiz-star-on" : "quiz-star-off"} />
                    ))}
                </div>
                <button className="btn btn-primary quiz-retry-btn" onClick={buildQuestions}>
                    <RefreshIcon className="icon-sm" />
                    Play Again
                </button>
            </div>
        );
    }

    if (!questions.length) return null;

    const q = questions[currentQ];

    return (
        <div className="quiz-container">
            {/* Mode tabs — hidden when driven by GamesHub (forcedMode) */}
            {!forcedMode && (
            <div className="quiz-mode-tabs">
                <button
                    className={`quiz-mode-tab${selectedMode === "recognition" ? " active" : ""}`}
                    onClick={() => handleModeChange("recognition")}
                >
                    <HearingIcon style={{ fontSize: 16 }} />
                    <span>Hear &amp; Spell</span>
                </button>
                <button
                    className={`quiz-mode-tab${selectedMode === "meaning" ? " active" : ""}${!hasMeaningMode ? " disabled" : ""}`}
                    onClick={() => hasMeaningMode && handleModeChange("meaning")}
                    disabled={!hasMeaningMode}
                    title={!hasMeaningMode ? "No meanings available for this list" : ""}
                >
                    <MenuBookIcon style={{ fontSize: 16 }} />
                    <span>Match Meaning</span>
                </button>
            </div>
            )}

            {/* Header */}
            <div className="quiz-header">
                <div className="quiz-progress-text">
                    {currentQ + 1} <span className="quiz-progress-sep">/</span> {questions.length}
                </div>
                <div className="quiz-score-badge">
                    <StarIcon className="quiz-star-badge-icon" />
                    <span>{results.filter(Boolean).length}</span>
                </div>
            </div>

            {/* Progress bar */}
            <div className="quiz-progress-bar">
                <div
                    className="quiz-progress-fill"
                    style={{ width: `${(currentQ / questions.length) * 100}%` }}
                />
            </div>

            {/* Question card */}
            <div className="quiz-question-card">
                {q.quizMode === "recognition" ? (
                    <>
                        <p className="quiz-instruction">Which word did you hear?</p>
                        <button className="quiz-play-btn" onClick={() => speak(q.questionText, "en-SG")}>
                            <VolumeUpIcon />
                            <span>Play Word</span>
                        </button>
                    </>
                ) : q.quizMode === "chinese" && q.chineseSubMode === "recognition" ? (
                    <>
                        <p className="quiz-instruction">Which character did you hear?</p>
                        <button className="quiz-play-btn" onClick={() => speak(q.questionText, "zh-CN")}>
                            <VolumeUpIcon />
                            <span>Play</span>
                        </button>
                    </>
                ) : q.quizMode === "chinese" ? (
                    <>
                        <p className="quiz-instruction">What does this mean?</p>
                        <div className="quiz-word quiz-chinese-char">{q.questionText}</div>
                        {q.questionPinyin && (
                            <div className="quiz-chinese-pinyin">{q.questionPinyin}</div>
                        )}
                        <button className="quiz-play-btn-sm" onClick={() => speak(q.questionText, "zh-CN")}>
                            <VolumeUpIcon className="icon-sm" />
                        </button>
                    </>
                ) : (
                    <>
                        <p className="quiz-instruction">What does this word mean?</p>
                        <div className="quiz-word">{q.questionText}</div>
                        <button className="quiz-play-btn-sm" onClick={() => speak(q.questionText, "en-SG")}>
                            <VolumeUpIcon className="icon-sm" />
                        </button>
                    </>
                )}
            </div>

            {/* Choices */}
            <div className="quiz-choices">
                {q.choices.map((choice, i) => {
                    let cls = "quiz-choice";
                    if (selected !== null) {
                        if (choice === q.correct) cls += " correct";
                        else if (choice === selected) cls += " incorrect";
                        else cls += " dimmed";
                    }
                    return (
                        <button
                            key={i}
                            className={cls}
                            onClick={() => handleSelect(choice)}
                            disabled={selected !== null}
                        >
                            <span className="quiz-choice-letter">{["A", "B", "C", "D"][i]}</span>
                            <span className={`quiz-choice-text${q.chineseSubMode === "recognition" ? " quiz-choice-chinese" : ""}`}>
                                {choice}
                            </span>
                            {selected !== null && choice === q.correct && (
                                <CheckCircleIcon className="quiz-choice-icon correct-icon" />
                            )}
                            {selected !== null && choice === selected && choice !== q.correct && (
                                <CancelIcon className="quiz-choice-icon wrong-icon" />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Feedback */}
            {selected !== null && (
                <div className={`quiz-feedback ${selected === q.correct ? "feedback-correct" : "feedback-wrong"}`}>
                    {selected === q.correct ? (
                        <><CheckCircleIcon className="icon-sm" /> Correct! Keep it up!</>
                    ) : (
                        <><CancelIcon className="icon-sm" /> Answer: <strong>{q.correct}</strong></>
                    )}
                </div>
            )}
        </div>
    );
}
